import React from 'react';
import { withRouter } from 'react-router-dom';
import { get, sortBy, find } from 'lodash';
import { Paper, IconButton } from '@mui/material';
import { connect } from 'react-redux';
import { nanoid } from 'nanoid';
import MUIDataTable from 'mui-datatables';
import { bindActionCreators } from 'redux';
import Stop from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import CustomToolbar from './custom-toolbar';
import withPersistentSettings from '../common/table-with-persistent-settings';
import WrappedSpinner from '../common/wrapped-spinner';
import {
  shouldFetchList,
  TaskTypes,
  AVAILABLE,
  IN_POSSESSION,
  IN_FIELD,
} from '../../lib/utils';
import locationActions from '../../actions/locations';
import zoneActions from '../../actions/zones';
import vehicleActions from '../../actions/vehicles';
import pickupZoneActions from '../../actions/pickupZones';
import vehicleZoneActions from '../../actions/vehicleZones';
import * as snackbarActions from '../../actions/snackbar';
import { RouteBuilderModal } from './routes/route-builder-modal';

const styles = {
  root: {
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
};

const NAME = 'Name';
const COLOR = 'Color';
const CURRENT = 'Current Target';
const OVERALL = 'Overall Target';

class ZoneTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editing: false,
      isRouteBuilderOpen: false,
      editRoute: {},
    };

    this.handleAddRouteBuilder = this.handleAddRouteBuilder.bind(this);
    this.handleEditRouteBuilder = this.handleEditRouteBuilder.bind(this);
    this.handleCloseBuilder = this.handleCloseBuilder.bind(this);
    this.handleSaveBuilder = this.handleSaveBuilder.bind(this);
    this.getLocationsForRoute = this.getLocationsForRoute.bind(this);
    this.getChangedRoutes = this.getChangedRoutes.bind(this);
  }

  componentDidMount() {
    const {
      zones,
      fetchZones,
      match,
      marketVehicles,
      fetchVehicles,
      marketLocations,
      fetchLocations,
      geofences,
      fetchVehicleZones,
      pickupZones,
      fetchPickupZones,
    } = this.props;

    const marketId = get(match, 'params.marketId');

    if (marketId) {
      if (shouldFetchList(marketId, marketVehicles)) fetchVehicles({ market_id: marketId });
      if (shouldFetchList(marketId, zones)) fetchZones({ market_id: marketId });
      if (shouldFetchList(marketId, marketLocations)) fetchLocations({ market_id: marketId });
      if (shouldFetchList(marketId, geofences)) fetchVehicleZones(marketId);
      if (shouldFetchList(marketId, pickupZones)) fetchPickupZones(marketId);
    }
  }

  get zones() {
    const zones = get(this, 'props.zones', []);
    return sortBy(
      zones,
      (zone) => zone.name.toLowerCase(),
    );
  }

  get tableOptions() {
    const basicOptions = this.props.tableOptions;
    const { zones } = this.props;

    basicOptions.onRowClick = (rowData) => {
      const matchingZone = find(zones, (zone) => zone._id === rowData[0]);
      this.handleEditRouteBuilder(matchingZone);
    };

    basicOptions.customToolbar = () => (
      <CustomToolbar onClick={this.handleAddRouteBuilder} type="Add" />
    );

    return basicOptions;
  }

  get columns() {
    const { selectedFilters, primarySort, zones } = this.props;
    // the order of objects in columnData needs to match
    // the order of objects in data - both are referenced
    // on the render of the MUI datatable
    // columnData[0] is hidden from view, but contains the zoneId
    // and is used to allow the onRowClick above to know what zone
    // it is referencing

    const columnData = [
      {
        name: 'ID',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        name: NAME,
        options: {
          customFilterListOptions: {
            render: (v) => `${NAME}: ${v}`,
          },
        },
      },
      {
        name: COLOR,
        options: {
          customBodyRender: (data) => {
            if (data) {
              return (
                <Stop style={{ color: data }} />
              );
            } else {
              return '';
            }
          },
          customFilterListOptions: {
            render: (v) => `${COLOR}: ${v}`,
          },
        },
      },
      {
        name: CURRENT,
        options: {
          filter: false,
          sort: false,
          customBodyRender: (zoneId) => {
            const matchingZone = find(zones, (zone) => zone._id === zoneId);
            if (zoneId) {
              return (
                <>
                  {this.overallTarget(matchingZone) - this.vehicleCounts(matchingZone)}
                </>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: OVERALL,
        options: {
          filter: false,
          sort: false,
          customBodyRender: (zoneId) => {
            const matchingZone = find(zones, (zone) => zone._id === zoneId);
            if (zoneId) {
              return (
                <>
                  {this.overallTarget(matchingZone)}
                </>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: 'Route Builder',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (zoneId) => {
            const matchingZone = find(zones, (zone) => zone._id === zoneId);
            if (zoneId) {
              return (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    this.handleEditRouteBuilder(matchingZone);
                  }}
                >
                  <EditIcon />
                </IconButton>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: 'Actions',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (zoneId) => {
            const matchingZone = find(zones, (zone) => zone._id === zoneId);
            if (zoneId) {
              return (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    this.handleDeleteRoute(matchingZone);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              );
            } else {
              return '';
            }
          },
        },
      },
    ];

    return this.props.addToColumnData(columnData, selectedFilters, primarySort);
  }

  get data() {
    const data = this.zones.map((zone) => {
      const tableData = [
        get(zone, '_id', ''),
        get(zone, 'name', ''),
        get(zone, 'hexColor', ''),
        get(zone, '_id', ''),
        get(zone, '_id', ''),
        get(zone, '_id', ''),
        get(zone, '_id', ''),
      ];

      return tableData;
    });

    return data;
  }

  getLocationsForRoute(route) {
    const { marketLocations = [] } = this.props;
    const locations = [];
    for (const location of route.locations) {
      for (const loc of marketLocations) {
        if (loc._id === location) {
          locations.push(loc);
          break;
        }
      }
    }
    return locations;
  }

  getChangedRoutes(changedLocations, currentRoute) {
    const { zones } = this.props;
    const routes = {};
    for (const route of zones) {
      if (route !== currentRoute) {
        let changed = false;
        for (const location of changedLocations) {
          if (route.locations.includes(location)) {
            const index = route.locations.indexOf(location);
            route.locations.splice(index, 1);
            changed = true;
          }
        }
        if (changed) {
          routes[route._id] = route.locations;
        }
      }
    }
    return routes;
  }

  vehicleCounts(route) {
    const locations = this.getLocationsForRoute(route);
    let count = 0;
    for (const location of locations) {
      const vehicleCount = get(location, 'vehicleIds', []).length;
      count += vehicleCount;
    }
    return count;
  }

  overallTarget(route) {
    const locations = this.getLocationsForRoute(route);
    let current = 0;
    for (const location of locations) {
      const target = get(location, 'targetVehicleCounts.link', 0);
      current += target;
    }
    return current;
  }

  handleAddRouteBuilder() {
    const { marketId } = get(this, 'props.match.params');

    this.setState({
      editRoute: {
        _id: nanoid(24),
        marketId,
        locations: [],
      },
      isRouteBuilderOpen: true,
      editing: false,
    });
  }

  handleEditRouteBuilder(route) {
    this.setState({
      isRouteBuilderOpen: true,
      editRoute: route,
      editing: true,
    });
  }

  handleCloseBuilder() {
    this.setState({
      isRouteBuilderOpen: false,
      editing: false,
      editRoute: {},
    });
  }

  handleSaveBuilder(route, changedLocations) {
    const { editing } = this.state;
    const {
      showSuccessSnackbar, showErrorSnackbar, updateMultipleZones,
    } = this.props;
    const { marketId } = get(this, 'props.match.params');

    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    const changedRoutes = this.getChangedRoutes(changedLocations, route);

    updateMultipleZones(changedRoutes)
      .then(() => {
        if (editing) {
          this.props.updateZone(route, route._id)
            .then(() => showSuccessSnackbar('Successfully updated route'))
            .catch((err) => showErrorSnackbar(`Error updating route: ${err}`));
        } else {
          this.props.createZone(route)
            .then(() => showSuccessSnackbar('Successfully created route'))
            .catch((err) => showErrorSnackbar(`Error creating route: ${err}`));
        }
        this.setState({ isRouteBuilderOpen: false, editing: false, editRoute: {} });
      })
      .then(() => this.props.fetchZones(query))
      .then(() => this.props.fetchLocations({ market_id: marketId }))
      .catch((err) => showErrorSnackbar(`Unexpected Error: ${err}`));
  }

  handleDeleteRoute(route) {
    const { showSuccessSnackbar } = this.props;
    const { marketId } = get(this, 'props.match.params');

    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    const newRoute = {
      ...route,
      status: 'Deleted',
    };

    this.props.updateZone(newRoute, newRoute._id)
      .then(() => showSuccessSnackbar('Successfully deleted route'))
      .then(() => this.props.fetchZones(query))
      .then(() => this.props.fetchLocations({ market_id: marketId }));
  }

  render() {
    const {
      isLoading,
      getTitleFromData,
      marketLocations = [],
      geofences = [], pickupZones = [],
      marketVehicles = [],
    } = this.props;
    const {
      editRoute, isRouteBuilderOpen,
    } = this.state;
    const displayData = this.data;

    const filterVehicle = (vehicle) => {
      if (vehicle.tasks && vehicle.tasks.length) {
        if (vehicle.tasks.some((t) => t.task === TaskTypes.PICK_UP)) {
          return false;
        }
      }

      return vehicle.extStatus === AVAILABLE
        && vehicle.extDeploymentStatus === IN_FIELD
        && vehicle.extPossessionStatus === IN_POSSESSION;
    };
    const vehiclesToDisplay = marketVehicles.filter((vehicle) => filterVehicle(vehicle));
    const filteredLocations = marketLocations.filter((location) => !location.isWarehouse === true);

    return (
      <WrappedSpinner isLoading={isLoading}>
        <Paper style={styles.root} sx={{ mt: 3 }}>
          <MUIDataTable
            title={getTitleFromData('Routes', displayData)}
            columns={this.columns}
            data={displayData}
            options={this.tableOptions}
          />
          {isRouteBuilderOpen && editRoute && (
            <RouteBuilderModal
              open={isRouteBuilderOpen}
              allLocations={filteredLocations}
              handleSave={this.handleSaveBuilder}
              handleClose={this.handleCloseBuilder}
              getLocationsForRoute={this.getLocationsForRoute}
              route={editRoute}
              geofences={geofences}
              pickupZones={pickupZones}
              scooters={vehiclesToDisplay}
            />
          )}
        </Paper>
      </WrappedSpinner>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...zoneActions,
  ...vehicleActions,
  ...locationActions,
  ...snackbarActions,
  ...pickupZoneActions,
  ...vehicleZoneActions,
}, dispatch);

const mapStateToProps = ({
  zones, vehicles, markets, locations, pickupZones, vehicleZones,
}) => ({
  isLoading: vehicleZones.isLoading || zones.isLoading || locations.isLoading,
  zones: zones.noDynamicZones,
  marketVehicles: vehicles.marketVehicles,
  marketLocations: locations.activeMarketLocations,
  selectedMarket: markets.selectedMarket,
  geofences: vehicleZones.vehicleZones,
  pickupZones: pickupZones.activeMarketPickupZones,
});

export default withPersistentSettings(
  connect(mapStateToProps, mapDispatchToProps)(withRouter(ZoneTable)),
  'ZoneTable',
);
